/**
 * salaryDetail — gom nhóm bản kê chi tiết lương.
 *
 * Toàn bộ file này là hàm THUẦN: nhận dữ liệu máy chủ trả về, trả ra nhóm để
 * vẽ. Tuyệt đối không tính lại tiền. Máy chủ đã gắn sẵn tiền cho từng dòng và
 * có test khoá bất biến "cộng hết các dòng = số trên phiếu"; cộng lần thứ hai
 * ở đây là dựng thêm một nguồn sự thật thứ hai để hai bên lệch nhau — mà bên
 * nhân viên đọc là bên này.
 */

import type { components } from "@/lib/api/generated/schema";
import {
  formatPayUnitPrice,
  formatPayUnits,
  PAY_UNIT_NAME,
  POINT_RATE_VND,
} from "@/features/salary-rules-v2/pay-unit";

export type OrderLine = components["schemas"]["PayrollOrderAttribution"];
export type SurchargeLine = components["schemas"]["PayrollSurchargeAttribution"];

/** Một nhóm dòng cùng loại việc, kèm số cộng của chính nhóm đó. */
export type DetailGroup<T> = {
  /** Khoá gom nhóm — chuỗi `detail` của đơn, hoặc `kind` của phát sinh. */
  key: string;
  /** Nhãn tiếng Việt hiển thị cho người đọc. */
  label: string;
  lines: T[];
  /** Số lượng — với phát sinh là tổng `qty`, không phải số dòng. */
  count: number;
  /** Tiền người đọc nhận từ cả nhóm. */
  pay: number;
};

/**
 * Nhãn dự phòng cho khoản phát sinh KHÔNG do bảng luật sinh ra.
 *
 * Từ QĐ 0115, mỗi dòng phát sinh mang sẵn `ruleLabel` — tên kế toán tự đặt
 * trên màn Cơ chế lương — nên bảng dưới chỉ còn đúng một mục: ảnh sửa từ gói
 * trang phục của hậu kỳ, khoản duy nhất còn nằm ngoài bảng luật. Thêm nhãn
 * cứng mới vào đây là dựng lại bản thứ hai để lệch với sổ.
 */
export const SURCHARGE_KIND_LABELS: Record<string, string> = {
  costume_edit: "Ảnh sửa từ gói trang phục",
};

export function surchargeKindLabel(kind: string): string {
  return SURCHARGE_KIND_LABELS[kind] ?? kind;
}

/**
 * Nhãn loại việc trên dòng đơn.
 *
 * Máy chủ gửi sang chuỗi tiếng Việt sẵn ("Khách AT", "Makeup CV Pro"…) nên
 * mặc định là dùng thẳng; bảng này chỉ đổi vài chuỗi cho dễ đọc hơn với người
 * ngoài phòng. Chuỗi lạ vẫn hiện nguyên văn — thà hiện chữ máy chủ còn hơn
 * nuốt mất một nhóm việc vì chưa ai kịp thêm nhãn.
 */
const ORDER_DETAIL_LABELS: Record<string, string> = {
  "Khách AT": "Khách ảnh thẻ (AT)",
  "Makeup AT": "Makeup ảnh thẻ (AT)",
};

export function orderDetailLabel(detail: string): string {
  if (!detail) return "Khác";
  return ORDER_DETAIL_LABELS[detail] ?? detail;
}

/** Cộng tiền của một loạt dòng. Chỉ cộng cái máy chủ đã gắn, không nhân chia. */
export function sumPay(lines: { pay?: number }[]): number {
  return lines.reduce((total, l) => total + (l.pay ?? 0), 0);
}

/** Một mức đơn giá trong nhóm: `count` đơn vị việc, mỗi đơn vị `unitPay` đồng. */
export type PriceTier = { count: number; unitPay: number };

/** Quá chừng này mức thì dòng tóm tắt dài hơn chính cái bảng nó tóm tắt. */
const MAX_PRICE_TIERS = 3;

/**
 * Tách tiền của một nhóm thành các mức "số lượng × đơn giá", để bản kê ghi
 * được "12 khách × 3 thạch lương thảo (= 30.000đ)" (CEO chốt 15b 16/09/2026).
 *
 * Đơn giá lấy từ CHÍNH tiền máy chủ gắn cho từng dòng chia cho số lượng của
 * dòng đó — không đọc biểu giá, không tính lại lương. Nên cộng các mức lại
 * luôn ra đúng tổng tiền của nhóm.
 *
 * Trả `null` khi không nói gọn được mà vẫn đúng: có dòng tiền không chia hết
 * cho số lượng (làm tròn là in ra phép nhân không ra đúng tiền — Câu 13), số
 * lượng không dương, hoặc quá nhiều mức giá (buổi chụp tính theo công, mỗi
 * buổi một số tiền).
 */
export function priceTiers(parts: { qty: number; pay: number }[]): PriceTier[] | null {
  const byPrice = new Map<number, number>();
  for (const { qty, pay } of parts) {
    if (qty <= 0 || pay % qty !== 0) return null;
    const unitPay = pay / qty;
    byPrice.set(unitPay, (byPrice.get(unitPay) ?? 0) + qty);
  }
  if (byPrice.size === 0 || byPrice.size > MAX_PRICE_TIERS) return null;
  return [...byPrice]
    .map(([unitPay, count]) => ({ count, unitPay }))
    .sort((a, b) => b.count - a.count || b.unitPay - a.unitPay);
}

/** "12 khách × 3 thạch lương thảo (= 30.000đ)"; bỏ danh từ thì "5 × …". */
export function priceTierText(tier: PriceTier, noun?: string): string {
  const count = noun ? `${tier.count} ${noun}` : String(tier.count);
  return `${count} × ${formatPayUnitPrice(tier.unitPay)}`;
}

/**
 * Xếp dòng bản kê theo ngày chụp, ngày sớm đứng trước — kiểu xem "Theo ngày"
 * (CEO chốt 1a · 2b, 30/10/2026: nhân sự dò lại tháng theo ngày, bảng chia
 * nhóm theo gói làm ngày nhảy qua lại giữa các nhóm).
 *
 * Cùng ngày thì giữ nguyên thứ tự máy chủ gửi (sort của JS ổn định). Dòng
 * không có ngày xuống cuối: đứng đầu là người đọc tưởng đó là việc đầu tháng.
 */
export function sortByShootDate<T extends { shootDate?: string | undefined }>(lines: T[]): T[] {
  return [...lines].sort((a, b) => {
    const da = a.shootDate ?? "";
    const db = b.shootDate ?? "";
    if (da === db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da < db ? -1 : 1;
  });
}

/**
 * Gom danh sách đơn theo loại việc, giữ nguyên thứ tự máy chủ trả về.
 *
 * Giữ thứ tự gốc chứ không sắp lại: máy chủ đã trả đơn theo ngày chụp tăng
 * dần, nên nhóm nào gặp trước là nhóm có việc sớm nhất — đọc xuôi theo thời
 * gian đúng cách người ta nhớ lại tháng của mình.
 */
export function groupOrderLines(lines: OrderLine[] = []): DetailGroup<OrderLine>[] {
  const byKey = new Map<string, DetailGroup<OrderLine>>();
  for (const line of lines) {
    const key = line.detail ?? "";
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: orderDetailLabel(key), lines: [], count: 0, pay: 0 };
      byKey.set(key, group);
    }
    group.lines.push(line);
    group.count += 1;
    group.pay += line.pay ?? 0;
  }
  return [...byKey.values()];
}

/** Tên loại của một khoản phát sinh: tên rule kế toán đặt, thiếu thì rơi về khoá. */
export function surchargeLineLabel(line: SurchargeLine): string {
  return line.ruleLabel?.trim() || surchargeKindLabel(line.kind);
}

/**
 * Gom phát sinh theo loại.
 *
 * Gom theo `kind` (khoá rule, máy chủ sinh) chứ KHÔNG theo `label` — label là
 * chữ người dùng tự gõ trên đơn, "Ghép phông" và "ghep phong" mà gom theo
 * label thì một hàng của bảng vỡ làm hai.
 *
 * NHÃN thì lấy `ruleLabel` — tên kế toán đặt cho rule đó trên màn Cơ chế lương
 * (QĐ 0115). Đổi tên rule là bản kê đổi theo, không chờ một đợt phát hành.
 *
 * `count` cộng theo `qty` chứ không đếm dòng: một gói trang phục cao cấp là
 * MỘT dòng nhưng đáng 5 ảnh sửa, đếm dòng thì bảng nói "1 ảnh" cạnh số tiền
 * của 5 ảnh.
 */
export function groupSurchargeLines(
  lines: SurchargeLine[] = [],
): DetailGroup<SurchargeLine>[] {
  const byKind = new Map<string, DetailGroup<SurchargeLine>>();
  for (const line of lines) {
    const key = line.kind;
    let group = byKind.get(key);
    if (!group) {
      group = { key, label: surchargeLineLabel(line), lines: [], count: 0, pay: 0 };
      byKind.set(key, group);
    }
    group.lines.push(line);
    group.count += line.qty && line.qty > 0 ? line.qty : 1;
    group.pay += line.pay ?? 0;
  }
  return [...byKind.values()];
}

/**
 * Đơn CHƯA hoàn thành hay chưa.
 *
 * Tiền của dòng này vẫn được tính vào lương — cờ chỉ để CẢNH BÁO người đọc
 * rằng đơn còn dở, đừng nhầm là khoản trừ. Chuỗi rỗng nghĩa là máy chủ không
 * gửi trạng thái (phòng ban không gắn), lúc đó KHÔNG cảnh báo: bôi vàng cả
 * bảng vì thiếu dữ liệu là biến cảnh báo thành nhiễu, rồi không ai nhìn nữa.
 *
 * Luật này đến từ nhánh demo (`isUnfinishedOrder`) — giữ nguyên từng chữ khi
 * chuyển sang bản kê mới, đừng "cải tiến" thành so danh sách trạng thái.
 */
export function isUnfinishedOrder(status: string | undefined): boolean {
  const normalized = (status ?? "").trim().toLowerCase();
  return normalized !== "" && normalized !== "completed";
}

/**
 * Dòng trang điểm buổi chụp mà ô "số người trang điểm" của đơn KHÁC tổng số mặt
 * cả ê-kíp khai ở khối chia việc (CEO chốt 4a, 03/11/2026 — CA-2610-508 ghi 20,
 * khối khai 3). Máy vẫn tính theo ô của đơn (QĐ 0194 điều 3); câu này chỉ để
 * bên tính lương thấy đơn cần hỏi lại. Một trong hai bên chưa khai ⇒ im.
 */
export function makeupHeadcountMismatchNote(line: OrderLine): string | null {
  const head = line.craftMakeupHeadcount ?? 0;
  const all = line.craftMakeupFacesAll ?? 0;
  if (head <= 0 || all <= 0 || head === all) return null;
  return `Đơn ghi ${head} người trang điểm, phần chia việc khai ${all} — hỏi lại quản lý trực tiếp, máy đang tính theo số trên đơn`;
}

/**
 * Ngày chụp dạng dd/mm — đủ để đối chiếu trong một tháng, ngắn để bảng không
 * tràn ngang trên điện thoại. Chuỗi lạ trả về nguyên văn thay vì "Invalid Date".
 */
export function shortDate(iso: string | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}`;
}

/**
 * Các ô giải thích mà dòng đơn và dòng phát sinh cùng có — bảng phát sinh in
 * phép tính y như bảng đơn (quyết định 0179, E3). `craftJobUnits` chỉ dòng đơn
 * doanh nghiệp mang; dòng phát sinh không có ô ấy.
 */
type CraftNoteLine = Pick<
  OrderLine,
  | "craftTier"
  | "craftCoefficient"
  | "craftSeniorityCut"
  | "craftUnits"
  | "craftAnchor"
  | "craftCrewSize"
  | "craftFloorTopUp"
  | "craftJobUnits"
  | "craftMakeupFaces"
  | "craftMakeupFacesAll"
  | "pay"
>;

/**
 * Thạch lương thảo của một mức CHƯA làm tròn — phần của một người trước khi
 * nhân bậc có thể lẻ vô hạn (một phần ba đơn là 6,666…). Chỉ để ĐỌC: tối đa
 * hai chữ số thập phân, bỏ số 0 thừa, dấu phẩy kiểu Việt. Không dùng cho tiền
 * đã trả — tiền máy gắn là đồng nguyên, `formatPayUnits` in đúng từng đồng.
 */
function formatRoundedPayUnits(vnd: number): string {
  return (vnd / POINT_RATE_VND).toFixed(2).replace(/\.?0+$/, "").replace(".", ",");
}

/**
 * Lời giải thích ngắn cho đơn giá theo bậc tay nghề: `"G2 · ×0,83"`.
 *
 * Trả `null` khi cơ chế bậc KHÔNG chạm vào dòng này — tức hầu hết mọi dòng hôm
 * nay, vì công tắc còn tắt. Nhờ vậy bảng trông y hệt bây giờ cho tới đúng ngày
 * bật, không phải chờ ai gỡ thêm dòng mã nào.
 *
 * Hệ số in ra hai chữ số thập phân, dấu phẩy kiểu Việt. Cố ý KHÔNG dùng
 * `toLocaleString`: bài kiểm chạy trong môi trường giả lập trình duyệt có thể
 * thiếu dữ liệu vùng miền, rồi ra dấu chấm — thước hỏng chứ không phải mã hỏng.
 */
export function craftTierNote(line: CraftNoteLine): string | null {
  if (!line.craftTier) return null;
  const parts: string[] = [line.craftTier];
  if (line.craftCoefficient) {
    parts.push(`×${line.craftCoefficient.toFixed(2).replace(".", ",")}`);
  }
  if (line.craftSeniorityCut) parts.push("chưa đủ thâm niên");
  return parts.join(" · ");
}

/**
 * Vế đầu của phép tính một dòng máy tính theo công: phần của người này, ghi
 * bằng thạch lương thảo — `"20 thạch lương thảo"`, hay
 * `"6,67 thạch lương thảo (đơn chia đều 3 thợ)"`.
 *
 * Phiếu KHÔNG ghi số công (CEO duyệt bản phác 22/09/2026, lượt 6 quyết định
 * 0179: "không hiện số công. chỉ hiện số thạch lương thảo thôi"). Con số đổi
 * thẳng từ hai ô máy gửi — phần công của người này × đơn giá một công của vai —
 * không đọc biểu giá, không tính lại gì.
 *
 * Đọc xuôi ba dòng là ra phép tính: phần của mình, rồi bậc × hệ số
 * (`craftTierNote`), rồi kết quả (`craftResultNote`). Chỉ có ở dòng máy thật sự
 * tính theo công (buổi chụp của dòng Chụp Ảnh; đơn profile, doanh nghiệp, khách
 * ảnh thẻ và phát sinh chụp từ 01/10/2026). Dòng đơn giá phẳng có bậc nhưng
 * KHÔNG có công ⇒ `null`: in phần ở đó là bịa ra một phép tính máy không làm.
 *
 * `craftUnits` máy gửi ĐÃ là phần của riêng người này; số thợ chỉ để người đọc
 * hiểu vì sao phần của mình là số lẻ — tuyệt đối không chia lại ở đây. Dòng
 * doanh nghiệp không nói "đơn chia": dòng cả việc (`craftCrewNote`) phía trên
 * đã nói số thợ cần và số người đi.
 */
export function craftUnitsNote(line: CraftNoteLine): string | null {
  if (!line.craftTier || !line.craftUnits || !line.craftAnchor) return null;
  const crew = line.craftCrewSize ?? 0;
  // Đã chia theo lời khai số mặt (QĐ 0194) thì không phải "chia đều N thợ".
  const declared = (line.craftMakeupFaces ?? 0) > 0 && (line.craftMakeupFacesAll ?? 0) > 0;
  const shared =
    crew > 1 && !((line.craftJobUnits ?? 0) > 0)
      ? declared
        ? ` (mình khai ${line.craftMakeupFaces}/${line.craftMakeupFacesAll} mặt của buổi)`
        : ` (đơn chia đều ${crew} thợ)`
      : "";
  return `${formatRoundedPayUnits(line.craftUnits * line.craftAnchor)} ${PAY_UNIT_NAME}${shared}`;
}

/** "1 nữ · 2 bé" — bỏ loại bằng 0; cả ba bằng 0 thì "0 khách". */
function makeupMixText(mix: { female: number; male: number; child: number }): string {
  const parts = [
    mix.female ? `${mix.female} nữ` : "",
    mix.male ? `${mix.male} nam` : "",
    mix.child ? `${mix.child} bé` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "0 khách";
}

/**
 * Dòng TRANG ĐIỂM buổi chụp tách vế đầu thành hai dòng (QĐ 0490, CEO chốt 3a
 * 30/10/2026): phần KHÁCH người này làm, và phần phát sinh của buổi (thêm
 * layout, tóc…) chia đều cho thợ —
 * `["Khách mình làm (1 nữ): 30 thạch lương thảo",
 *   "Layout / phát sinh ÷ 2 thợ: 45 thạch lương thảo"]`.
 *
 * Trước bản này phiếu in một cục "75 thạch lương thảo (đơn chia 2 thợ)" và
 * thợ đọc thành "một khách nữ 750k". Hai số máy gửi cộng lại đúng bằng
 * `craftUnits` — ở đây chỉ nhân đơn giá, không chia lại.
 *
 * `null` khi dòng không phải dòng trang điểm buổi chụp hoặc không có gì để
 * tách (không phát sinh, chưa chia theo loại khách) — phiếu giữ một dòng như
 * `craftUnitsNote`.
 */
export function craftMakeupUnitsNotes(line: OrderLine): string[] | null {
  if (craftUnitsNote(line) === null || !line.craftAnchor) return null;
  const faces = line.craftMakeupFaceUnits ?? 0;
  const extra = line.craftMakeupExtraUnits ?? 0;
  const mine = line.craftMakeupMine;
  if (extra <= 0 && !mine) return null;
  const crew = line.craftCrewSize ?? 0;
  const who = mine
    ? `Khách mình làm (${makeupMixText(mine)})`
    : crew > 1
      ? `Phần khách (đơn chia ${crew} thợ)`
      : "Phần khách";
  const notes = [`${who}: ${formatRoundedPayUnits(faces * line.craftAnchor)} ${PAY_UNIT_NAME}`];
  if (extra > 0) {
    const split = crew > 1 ? ` ÷ ${crew} thợ` : "";
    notes.push(`Layout / phát sinh${split}: ${formatRoundedPayUnits(extra * line.craftAnchor)} ${PAY_UNIT_NAME}`);
  }
  return notes;
}

/** "(cần 3 thợ) ÷ 2 người đi" — vế chia chung của mọi dòng doanh nghiệp. */
function crewSplitText(need: number, went: number): string {
  return `(cần ${need} thợ) ÷ ${went} người đi`;
}

/**
 * Dòng đầu của một đơn doanh nghiệp: số thợ báo giá ghi là cần và số người
 * thật sự đi. Hai dạng đơn, hai câu:
 *
 * - Tính theo công (giá chung): thêm cả việc bằng thạch lương thảo —
 *   `"720 thạch lương thảo cả việc (cần 3 thợ) ÷ 2 người đi"`.
 * - Đơn có GIÁ RIÊNG (`crewRate`): máy không quy ra công nên không có phép
 *   tính nào để in, nhưng vẫn chia cần ÷ đi ⇒
 *   `"Giá riêng của đơn (cần 3 thợ) ÷ 2 người đi"` (CEO chốt câu 2a
 *   23/09/2026). Không nói ra thì thợ thấy tiền thấp hơn giá đã thoả thuận mà
 *   không có lời giải thích nào trên phiếu — đúng chỗ người ta nghĩ mình bị trừ.
 *
 * Báo giá chưa ghi số thợ cần thì máy tính mỗi người như MỘT thợ làm trọn —
 * nói thẳng ra, kẻo người đọc tưởng đã chia. Dòng khác (không mang số cả việc,
 * không phải giá riêng đã chia) ⇒ `null`.
 *
 * `craftCrewSize` là cổng của dạng giá riêng: ngã cụt (cơ chế chưa chạm người
 * này) máy XOÁ số thợ và số cần khỏi dòng vì nó trả đủ, không chia — nói "÷"
 * ở đó là dựng ra một phép chia máy không làm.
 */
/**
 * Chi tiết công của một BUỔI chụp theo bậc (thợ chụp): set gốc, set phát sinh
 * — gồm set mua thêm theo gói chưa tách lịch (QĐ 0506) — nấc thang tháng, và
 * phần tiền phẳng. Đọc thẳng `craftSession` / `craftFlatExtras` máy gửi, không
 * tính lại. Dòng không phải buổi chụp theo bậc ⇒ mảng rỗng.
 */
export function craftSessionNotes(line: OrderLine): string[] {
  const s = line.craftSession;
  const anchor = line.craftAnchor ?? 0;
  const out: string[] = [];
  if (s && anchor > 0 && (s.TotalUnits ?? 0) > 0) {
    out.push(`Set gốc: ${formatRoundedPayUnits((s.BaseUnits ?? 0) * anchor)} ${PAY_UNIT_NAME}`);
    if ((s.ExtraSets ?? 0) > 0) {
      const ratio = Math.round((s.ExtraRatio ?? 0) * 100);
      out.push(
        `${s.ExtraSets} set phát sinh / set mua thêm theo gói (nấc ${ratio}% theo số set cả tháng): ${formatRoundedPayUnits((s.ExtraUnits ?? 0) * anchor)} ${PAY_UNIT_NAME}`,
      );
    }
    if ((s.SurchargeUnits ?? 0) > 0) {
      out.push(`Mục phát sinh tính công: ${formatRoundedPayUnits((s.SurchargeUnits ?? 0) * anchor)} ${PAY_UNIT_NAME}`);
    }
  }
  if ((line.craftFlatExtras ?? 0) > 0) {
    out.push(`Tiền phẳng (tóc, concept, book…): ${formatPayUnits(line.craftFlatExtras ?? 0)} ${PAY_UNIT_NAME}`);
  }
  return out;
}

export function craftCrewNote(line: OrderLine): string | null {
  const job = line.craftJobUnits ?? 0;
  const need = line.crewNeeded ?? 0;
  const went = line.craftCrewSize ?? 0;
  if (job <= 0 || !line.craftAnchor) {
    if ((line.crewRate ?? 0) <= 0 || went <= 0) return null;
    if (need <= 0) return "Chưa có số thợ cần — tính như một thợ";
    return `Giá riêng của đơn ${crewSplitText(need, went)}`;
  }
  if (need <= 0) return "Chưa có số thợ cần — tính như một thợ";
  return `${formatRoundedPayUnits(job * line.craftAnchor)} ${PAY_UNIT_NAME} cả việc ${crewSplitText(need, went || 1)}`;
}

/**
 * Phần MỨC RIÊNG của một dòng doanh nghiệp: `"Mức riêng: +30 thạch lương thảo"`.
 *
 * Kế toán thoả thuận riêng với một người mấy trăm nghìn một buổi (thẻ Theo nhân
 * sự). Khoản ấy KHÔNG nhân bậc, nhưng vẫn chia theo số thợ cần ÷ số người đi,
 * nên máy đóng dấu phần đã chia lên dòng và phiếu in nó thành MỘT dòng: đọc
 * xuôi mới cộng lại ra đúng số tiền bên cạnh (CEO chốt câu 3a 23/09/2026).
 * Không có dòng này thì kết quả lớn hơn phép tính bậc ngay bên trên mà không
 * lời giải thích nào — người đọc chỉ còn cách tin.
 *
 * Số âm cũng in (thoả thuận trừ bớt): dấu nằm cạnh con số như dòng rào bù, để
 * hai dòng cùng đọc một kiểu. Chỉ nói ở dòng CÓ in phép tính — thêm một mình
 * nó dưới dòng không có phép tính nào là treo một số cộng không có gì để cộng.
 */
export function craftOverrideNote(line: OrderLine): string | null {
  const extra = line.craftStaffOverride ?? 0;
  if (!extra) return null;
  if (craftUnitsNote(line) === null) return null;
  const sign = extra > 0 ? "+" : "−";
  return `Mức riêng: ${sign}${formatPayUnits(Math.abs(extra))} ${PAY_UNIT_NAME}`;
}

/**
 * Dòng cuối của phép tính: `"= 16,7 thạch lương thảo"`.
 *
 * Lấy từ tiền máy ĐÃ gắn cho dòng, trừ phần rào bù (nếu có) — tức đúng phần
 * bậc tính ra, cộng với dòng bù bên dưới là ra số tiền in cạnh. Không nhân lại
 * phần × hệ số ở đây: máy làm tròn một lần tới nghìn đồng, nhân lại là ra một
 * con số thứ hai lệch vài trăm đồng với chính dòng tiền.
 *
 * Dòng doanh nghiệp của người có thoả thuận riêng thì tiền máy gắn đã gồm phần
 * thoả thuận, nên kết quả lớn hơn phần × hệ số — `craftOverrideNote` in phần
 * chênh ấy ngay bên trên để người đọc cộng lại đúng số tiền.
 */
export function craftResultNote(line: CraftNoteLine): string | null {
  if (craftUnitsNote(line) === null) return null;
  return `= ${formatPayUnits((line.pay ?? 0) - (line.craftFloorTopUp ?? 0))} ${PAY_UNIT_NAME}`;
}

/**
 * Phần RÀO CHUYỂN ĐỔI bù thêm cho một dòng tính theo công:
 * `"Bù giữ mức cũ: +3,4 thạch lương thảo"` — cùng đơn vị với phép tính phía
 * trên, nên kết quả cộng phần bù là đúng số tiền của dòng.
 *
 * Khi bậc mới tính ra ÍT hơn mức người đó vẫn nhận, máy trả mức cũ. Không nói
 * ra phần bù là để kết quả và số tiền chỏi nhau trên cùng một dòng.
 *
 * Cố ý chỉ nói ở dòng có in phép tính (`craftUnitsNote` khác null). Dòng đơn
 * giá phẳng không in phép tính nào nên không có gì chỏi nhau; thêm dòng chữ ở
 * đó là đổi phiếu lương của các khối chưa ai bàn tới.
 */
export function craftFloorNote(line: CraftNoteLine): string | null {
  if (!line.craftFloorTopUp || line.craftFloorTopUp <= 0) return null;
  if (craftUnitsNote(line) === null) return null;
  return `Bù giữ mức cũ: +${formatPayUnits(line.craftFloorTopUp)} ${PAY_UNIT_NAME}`;
}

/**
 * Dòng phát sinh trả tiền phẳng mà máy ĐÃ chia đều cho số thợ cùng vai:
 * `"thạch lương thảo phẳng · chia 2 thợ"`. Tiền phẳng không nhân bậc nên không
 * có phép tính nào khác để ghi.
 *
 * Ngã cụt (cơ chế chưa chạm người này) máy vẫn ghi số thợ nhưng trả đủ, không
 * chia — nên chỉ tin cờ `craftFlatShare`, không suy từ số thợ.
 */
export function craftFlatShareNote(line: SurchargeLine): string | null {
  const crew = line.craftCrewSize ?? 0;
  if (!line.craftFlatShare || crew <= 1) return null;
  return `${PAY_UNIT_NAME} phẳng · chia ${crew} thợ`;
}

/**
 * Các dòng chú thích dưới số tiền của một dòng đơn. Đọc xuôi từ trên xuống là
 * ra phép tính, chỉ bằng thạch lương thảo (CEO duyệt bản phác 22/09/2026 —
 * không ghi số công): cả việc doanh nghiệp, phần của mình, bậc × hệ số, mức
 * thoả thuận riêng (nếu có), kết quả, rồi phần rào bù (nếu có). Dòng trả theo
 * đơn giá phẳng chỉ có dòng bậc — y như trước quyết định 0174.
 *
 * Dùng chung giữa phiếu lương và thẻ "Lương" của chi tiết đơn: một người đọc
 * hai màn phải thấy cùng một phép tính.
 */
export function orderLinePayNotes(line: OrderLine): string[] {
  return [
    craftCrewNote(line),
    // Dòng trang điểm buổi chụp: khách mình làm + layout chia đều (QĐ 0490).
    ...(craftMakeupUnitsNotes(line) ?? [craftUnitsNote(line)]),
    // Buổi chụp theo bậc: set gốc + set phát sinh + nấc tháng + tiền phẳng.
    ...craftSessionNotes(line),
    craftTierNote(line),
    craftOverrideNote(line),
    craftResultNote(line),
    craftFloorNote(line),
  ].filter((note): note is string => note !== null);
}

/**
 * Chú thích dưới số tiền của một khoản phát sinh. Từ buổi chụp 01/10/2026
 * (quyết định 0179, E3): dòng tính theo công in phép tính như bảng đơn; dòng
 * tiền phẳng đã chia thì nói chia mấy thợ. Dòng chưa chạm cơ chế thì rỗng.
 */
export function surchargeLinePayNotes(line: SurchargeLine): string[] {
  return [
    craftUnitsNote(line),
    craftTierNote(line),
    craftResultNote(line),
    craftFloorNote(line),
    craftFlatShareNote(line),
  ].filter((note): note is string => note !== null);
}
